import { useCallback, useEffect, useState } from 'react'
import { useHitNavigation, type HitNavigation } from './useHitNavigation'

export interface SearchTerm {
  /** What the user has typed — bound to the input. */
  input: string
  setInput: (value: string) => void
  /** The debounced, trimmed term the renderer highlights. */
  term: string
}

export interface PreviewSearch extends SearchTerm, HitNavigation {
  /** Enter walks forward, Shift+Enter back — the keys every find bar uses. */
  handleKey: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

/**
 * The typed value and the debounced term of a preview search (#627, #755).
 *
 * Debounced because every keystroke would otherwise re-run the highlight
 * pass over every page of a long PDF. `resetKey` identifies the document: a
 * different document invalidates the previous search.
 */
export function useSearchTerm(resetKey: string, seededTerm?: string): SearchTerm {
  const [input, setInput] = useState('')
  const [term, setTerm] = useState('')

  useEffect(() => {
    const trimmed = input.trim()
    if (!trimmed) {
      setTerm('')
      return
    }
    const id = window.setTimeout(() => setTerm(trimmed), 250)
    return () => window.clearTimeout(id)
  }, [input])

  useEffect(() => {
    setInput('')
  }, [resetKey])

  useEffect(() => {
    if (seededTerm) setInput(seededTerm)
  }, [seededTerm])

  return { input, setInput, term }
}

/** Enter steps forward, Shift+Enter back. */
export function useEnterSteps(next: () => void, previous: () => void) {
  return useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') return
      e.preventDefault()
      if (e.shiftKey) previous()
      else next()
    },
    [next, previous],
  )
}

/**
 * Full-text search state for a preview whose every hit is in the DOM at once
 * (the all-pages PDF renderer, markdown, code, tables).
 */
export function usePreviewSearch(
  contentRef: React.RefObject<HTMLElement | null>,
  resetKey: string,
  seededTerm?: string,
): PreviewSearch {
  const termState = useSearchTerm(resetKey, seededTerm)
  const navigation = useHitNavigation(contentRef, termState.term)
  const handleKey = useEnterSteps(navigation.next, navigation.previous)
  return { ...termState, ...navigation, handleKey }
}
